/**
 * For more details explanation see the navigation scope guide in the docs
 */
import { clone, isEmpty, notEmpty } from '~/utils/lib/core/prelude'
import { extractRouteLevelRecord, fromCompanyLevel, fromCurrentLevel, fromStructureLevel, navigationPerimeterBoilerplate, extractRouteNavigationScope } from '~/stores/layout/navigation'
import { NAVIGATION_SCOPE, STORE_PREFIX as LAYOUT_STORE_PREFIX } from '~/utils/lib/layout/types'
import storage from '~/utils/lib/core/storage'

/**
 * @param {import('~/utils/lib/layout/types').NAVIGATION_SCOPE|undefined} currentScope
 * @param {import('~/utils/lib/layout/types').NAVIGATION_SCOPE} expectedScope
 * @param {string} source
 */
function assertNavigationScope(currentScope, expectedScope, source) {
  if (currentScope === expectedScope)
    return

  throw new Error(
    `[navigation-scope] ${source} requires "${expectedScope}", received "${currentScope || 'undefined'}".`
  )
}

/**
 * @param {ReturnType<typeof useNavigationContext>} navigationContext
 */
function toDataOnlyContext(navigationContext) {
  return {
    navigationPerimeter: navigationContext.navigationPerimeter,
    navigatedStructureId: navigationContext.navigatedStructureId,
    navigatedCompanyCode: navigationContext.navigatedCompanyCode,
    navigationScope: navigationContext.navigationScope,
    fromStructureLevel: navigationContext.fromStructureLevel,
  }
}
/**
 * @param {ReturnType<typeof useNavigationContext>} navigationContext
 */
function toCompanyOptionalContext(navigationContext) {
  return {
    navigationPerimeter: navigationContext.navigationPerimeter,
    navigatedStructureId: navigationContext.navigatedStructureId,
    navigatedCompanyCode: navigationContext.navigatedCompanyCode,
    navigationScope: navigationContext.navigationScope,
    fromStructureLevel: navigationContext.fromStructureLevel,
    fromCurrentLevel: navigationContext.fromCurrentLevel,
  }
}
/**
 * @param {ReturnType<typeof useNavigationContext>} navigationContext
 */
function toCompanyRequiredContext(navigationContext) {
  return {
    navigationPerimeter: navigationContext.navigationPerimeter,
    navigatedStructureId: navigationContext.navigatedStructureId,
    navigatedCompanyCode: navigationContext.navigatedCompanyCode,
    navigationScope: navigationContext.navigationScope,
    fromStructureLevel: navigationContext.fromStructureLevel,
    fromCompanyLevel: navigationContext.fromCompanyLevel,
  }
}
/**
 * @desc
 *    Set up a navigation guard, providing navigation state for child
 *    Vue Components
 *
 *    This
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useNavigationGuard(
  partialNavigationState = {}
) {
  const route = useRoute()
  const navigationPerimeter = reactive({
    ...navigationPerimeterBoilerplate,
    structure_id: partialNavigationState.structure_id ?? '',
    company_code: partialNavigationState.company_code ?? '',
  })

  provide('navigationPerimeter', navigationPerimeter)

  watch(
    () => route.fullPath,
    () => Object.assign(
      navigationPerimeter,
      extractRouteLevelRecord(route)
    ),
    {
      immediate: true,
      flush: 'pre'
    }
  )

  watch(
    () => navigationPerimeter,
    ({
      structure_id,
      company_code,
    }) => {
      isEmpty(structure_id) ?
        storage.delete(LAYOUT_STORE_PREFIX, 'structure') :
        storage.set(LAYOUT_STORE_PREFIX, 'structure', structure_id)

      isEmpty(company_code) ?
        storage.delete(LAYOUT_STORE_PREFIX, 'company') :
        storage.set(LAYOUT_STORE_PREFIX, 'company', company_code)

    },
    {
      deep: true,
      immediate: true,
      flush: 'post'
    }
  )

  const navigationScope = computed(
    () => extractRouteNavigationScope(route)
  )
  const navigatedStructureId = computed(() => navigationPerimeter.structure_id ?? '')
  const navigatedCompanyCode = computed(() => navigationPerimeter.company_code ?? '')

  return {
    navigationPerimeter,
    navigatedStructureId,
    navigatedCompanyCode,
    navigationScope,
    fromStructureLevel: () => fromStructureLevel(navigationPerimeter),
    fromCompanyLevel: () => fromCompanyLevel(navigationPerimeter),
    fromCurrentLevel: () => fromCurrentLevel(navigationPerimeter),
  }
}

/**
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useDataOnlyNavigationGuard(partialNavigationState = {}) {
  const navigationContext = useNavigationGuard(partialNavigationState)

  assertNavigationScope(
    navigationContext.navigationScope.value,
    NAVIGATION_SCOPE.DATA_ONLY,
    'useDataOnlyNavigationGuard'
  )

  return toDataOnlyContext(navigationContext)
}

/**
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useCompanyRequiredNavigationGuard(partialNavigationState = {}) {
  const navigationContext = useNavigationGuard(partialNavigationState)

  assertNavigationScope(
    navigationContext.navigationScope.value,
    NAVIGATION_SCOPE.COMPANY_REQUIRED,
    'useCompanyRequiredNavigationGuard'
  )

  return toCompanyRequiredContext(navigationContext)
}

/**
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useCompanyOptionalNavigationGuard(partialNavigationState = {}) {
  const navigationContext = useNavigationGuard(partialNavigationState)

  assertNavigationScope(
    navigationContext.navigationScope.value,
    NAVIGATION_SCOPE.COMPANY_OPTIONAL,
    'useCompanyOptionalNavigationGuard'
  )

  return toCompanyOptionalContext(navigationContext)
}
/**
 * @desc Retrieve current navigation context
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useNavigationContext(
  partialNavigationState = {}
) {
  const route = useRoute()
  const navigationPerimeter = inject(
    'navigationPerimeter',
    clone(navigationPerimeterBoilerplate)
  )

  if (notEmpty(partialNavigationState)) {
    if ('structure_id' in partialNavigationState)
      navigationPerimeter.structure_id = partialNavigationState.structure_id ?? ''
    if ('company_code' in partialNavigationState)
      navigationPerimeter.company_code = partialNavigationState.company_code ?? ''
  }

  const navigationScope = computed(
    () => extractRouteNavigationScope(route)
  )
  const navigatedStructureId = computed(() => navigationPerimeter.structure_id ?? '')
  const navigatedCompanyCode = computed(() => navigationPerimeter.company_code ?? '')

  return {
    navigationPerimeter,
    navigatedStructureId,
    navigatedCompanyCode,
    navigationScope,
    fromStructureLevel: () => fromStructureLevel(navigationPerimeter),
    fromCompanyLevel: () => fromCompanyLevel(navigationPerimeter),
    fromCurrentLevel: () => fromCurrentLevel(navigationPerimeter),
  }
}

/**
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useDataOnlyNavigationContext(partialNavigationState = {}) {
  const navigationContext = useNavigationContext(partialNavigationState)

  assertNavigationScope(
    navigationContext.navigationScope.value,
    NAVIGATION_SCOPE.DATA_ONLY,
    'useDataOnlyNavigationContext'
  )

  return toDataOnlyContext(navigationContext)
}

/**
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useCompanyRequiredNavigationContext(partialNavigationState = {}) {
  const navigationContext = useNavigationContext(partialNavigationState)

  assertNavigationScope(
    navigationContext.navigationScope.value,
    NAVIGATION_SCOPE.COMPANY_REQUIRED,
    'useCompanyRequiredNavigationContext'
  )

  return toCompanyRequiredContext(navigationContext)
}

/**
 * @param {Partial<import('~/stores/layout/navigation').NavigationState>} [partialNavigationState={}]
 */
export function useCompanyOptionalNavigationContext(partialNavigationState = {}) {
  const navigationContext = useNavigationContext(partialNavigationState)

  assertNavigationScope(
    navigationContext.navigationScope.value,
    NAVIGATION_SCOPE.COMPANY_OPTIONAL,
    'useCompanyOptionalNavigationContext'
  )

  return toCompanyOptionalContext(navigationContext)
}
